import React, {useEffect} from "react";
import {useTranslate} from "@tolgee/react";
import {useForm} from "react-hook-form";
import Form, {BasicInput, EmailInput} from "view/components/Form";
import {
  useFormActionsContext,
  useFormStateContext,
} from "view/SignUp/SignUpToken/context";
import {emailValidationPattern} from "tools/validations";
import FlexBox from "view/components/Layout/FlexBox";
import ButtonGroup from "view/SignUp/SignUpToken/components/ButtonGroup";
import {isEqual} from "lodash";
import clsx from "clsx";
import {
  CredentialsFormState,
  ValidateExhibitorReason,
} from "view/SignUp/SignUpToken/context/types";
import {InputAdornment, Tooltip} from "@material-ui/core";
import Alert from "view/components/Alert";
import GoogleIcon from "view/images/GoogleIcon";
import applyGoogleEmail from "view/SignUp/SignUpToken/utils/applyGoogleEmail";
import resolveCredentialsSubmitAction from "view/SignUp/SignUpToken/utils/resolveCredentialsSubmitAction";
import ErrorMessage from "view/SignUp/SignUpToken/components/ErrorMessage";
import EmailInUseByAnotherRole from "view/SignUp/SignUpToken/components/EmailInUseByAnotherRole";
import {deepTrimValue} from "tools/common/trimFormValues";
import useLocalizedLocalMap from "hooks/useLocalizedLocalMap";
import {useParams} from "react-router-dom";
import useStyles from "./styles";

const isNonEmptyField = (value: string) => !!value.trim().length;

const Credentials = () => {
  const classes = useStyles();
  const {t} = useTranslate();
  const {
    credentials,
    show,
    validationResult,
    submitError,
    googleIdentity,
    googleError,
    isEmailInUseByAnotherRole,
    mustValidateCredentials,
    isGoogleBusy,
  } = useFormStateContext();
  const {
    nextStep,
    setCredentials,
    setCredentialsDraftReader,
    submitExhibitor,
    completeGoogleSignUp,
  } = useFormActionsContext();
  const {uid: token} = useParams();
  const localMap = useLocalizedLocalMap(show);

  const shouldWarn = (field: keyof CredentialsFormState) => {
    if (!validationResult) {
      return false;
    }
    return validationResult.status === "warning";
  };
  const onSubmit = async (data: CredentialsFormState) => {
    const trimmedFormValues = deepTrimValue(
      applyGoogleEmail(data, googleIdentity)
    );
    const action = resolveCredentialsSubmitAction({
      hasChanged: !isEqual(trimmedFormValues, credentials),
      mustValidate: mustValidateCredentials,
      lastStatus: validationResult?.status,
    });

    if (action === "next") {
      nextStep();
      return;
    }
    if (action === "ignore") {
      return;
    }
    setCredentials(trimmedFormValues);
    await submitExhibitor({...trimmedFormValues, showId: show.id, token}).catch(
      (e) => {
        console.error("Error submitting exhibitor: ", e);
      }
    );
  };

  const form = useForm<CredentialsFormState>({
    mode: "all",
    defaultValues: credentials,
  });
  const {formState, handleSubmit, setError, getValues} = form;
  const {isSubmitting, isValid} = formState;

  useEffect(() => {
    setCredentialsDraftReader(() => getValues());

    return () => setCredentialsDraftReader(null);
  }, [getValues, setCredentialsDraftReader]);

  const getInputClasses = (fieldName: keyof CredentialsFormState) =>
    clsx(classes.input, {[classes.warning]: shouldWarn(fieldName)});

  // A field error from `setError` does not count against `isValid`, and the
  // Google address cannot be edited away — only another account clears it.
  const isSubmitDisabled =
    !isValid || isSubmitting || isEmailInUseByAnotherRole;
  useEffect(() => {
    if (validationResult?.status !== "error") {
      return;
    }
    // Every other refusal is about the stand; this one is about the address.
    setError(
      validationResult.reason ===
        ValidateExhibitorReason.EmailInUseByAnotherRole
        ? "email"
        : "standNumber",
      {message: ""}
    );
  }, [validationResult, setError]);

  // Refused as soon as Google returned, before any validation ran.
  useEffect(() => {
    if (isEmailInUseByAnotherRole) setError("email", {message: ""});
  }, [isEmailInUseByAnotherRole, setError]);

  const localizedStand = localMap.stand;

  // "You already have an account for this stand" is not a dead end once the
  // person has proved who they are — it is the acceptance criteria's "existing
  // account" case, so it signs them in instead of showing an error.
  const isOwnStand =
    validationResult?.reason === ValidateExhibitorReason.OwnStand;
  const shouldSignInInstead = Boolean(googleIdentity) && isOwnStand;

  useEffect(() => {
    if (!shouldSignInInstead) return;

    completeGoogleSignUp();
  }, [shouldSignInInstead, validationResult, completeGoogleSignUp]);

  // The same email holds this stand under a different company name. Unlike the
  // own-stand case this stops and asks: the person may simply have mistyped the
  // company, so they choose between correcting it and signing in. Nothing is
  // created or renamed either way.
  const canContinueToAccount =
    Boolean(googleIdentity) &&
    validationResult?.reason ===
      ValidateExhibitorReason.StandHeldUnderOtherCompany;

  const isEmailRefusedForAnotherRole =
    validationResult?.reason ===
    ValidateExhibitorReason.EmailInUseByAnotherRole;

  return (
    <Form
      form={form}
      onSubmit={handleSubmit(onSubmit)}
      className={classes.form}
    >
      <FlexBox column gap={2} width="100%">
        <BasicInput
          type="text"
          name="companyName"
          label={t("global.sign_up.company_name")}
          className={getInputClasses("companyName")}
          required
          rules={{
            validate: isNonEmptyField,
          }}
        />
        <EmailInput
          required
          className={getInputClasses("email")}
          rules={{
            pattern: {
              value: emailValidationPattern,
              message: t("common.validation.email"),
            },
          }}
          id="email"
          name="email"
          label={t("common.field.email")}
          disabled={!!googleIdentity}
          InputProps={
            googleIdentity
              ? {
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={t("global.sign_up.email_from_google")}>
                        <span className={classes.googleMark}>
                          <GoogleIcon />
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }
              : undefined
          }
        />
        <BasicInput
          label={t("global.sign_up.stand_number_label", {
            stand: localizedStand,
          })}
          name="standNumber"
          id="standNumber"
          className={getInputClasses("standNumber")}
          required
          rules={{
            validate: isNonEmptyField,
          }}
        />
        {validationResult &&
          validationResult.status !== "success" &&
          !shouldSignInInstead && (
            <ErrorMessage
              result={validationResult}
              onContinueToAccount={
                canContinueToAccount ? completeGoogleSignUp : undefined
              }
              isContinuing={isGoogleBusy}
            >
              {isEmailRefusedForAnotherRole ? (
                <EmailInUseByAnotherRole
                  email={credentials.email}
                  isFromGoogle={Boolean(googleIdentity)}
                />
              ) : undefined}
            </ErrorMessage>
          )}

        {googleError && (
          <Alert severity="error" className={classes.googleAlert}>
            {googleError}
          </Alert>
        )}
        {submitError && (
          <ErrorMessage result={{status: "error", message: submitError}} />
        )}

        <ButtonGroup
          isSubmitDisabled={isSubmitDisabled}
          isSubmitting={isSubmitting}
        />
      </FlexBox>
    </Form>
  );
};

export default Credentials;
